/**
 * Body map layout — percentage coordinates for each selectable body part.
 *
 * All coordinates are expressed as percentages of the body container, so the
 * layout scales cleanly at any resolution without any JS measurement.
 *
 * Coordinate system:
 *   x: 0% = left edge, 100% = right edge of the body container
 *   y: 0% = top edge, 100% = bottom edge of the body container
 *
 * Shape tokens (CSS applied in BodyPartButton):
 *   "circle"  — round
 *   "oval-v"  — tall oval (arms, legs)
 *   "oval-h"  — wide oval (shoulders, hips)
 *   "pill"    — rounded pill (back, other)
 */

export const BODY_CONTAINER_ASPECT_RATIO = 0.55; // width / height

/**
 * Body image tuning — adjust these to align the SVG with the button grid
 * without touching any button coordinates.
 *
 * BODY_SCALE: 1.0 = body fills BodyStage height. Increase to grow.
 * BODY_OFFSET_X/Y: % offset from centre (positive = right / down).
 */
export const BODY_SCALE = 1.3;
export const BODY_OFFSET_X = 0; // %
export const BODY_OFFSET_Y = 0; // %

/**
 * Optional per-button position fine-tuning.
 *
 * Every BODY_PARTS entry accepts optional  tx  and  ty  strings that set the
 * X and Y arguments of translate() for that button only. Both default to
 * -50% which centres the button on its x/y coordinate.
 *
 *   ty: "-100%"  ← bottom edge at y (button sits entirely above)
 *   ty: "0%"     ← top edge at y (button sits entirely below)
 *   ty: "-80%"   ← mostly above y — good for wrists/hands
 *   tx: "-50%"   ← default, horizontally centred on x
 *
 * x/y/w/shape stay unchanged — tx/ty only affect the CSS transform anchor.
 * The :active (tap) scale(0.93) is appended automatically.
 */
export const BODY_PARTS = [
    // midline column
    { code: "neck", x: 50, y: 12, ty: "-32%", w: 20, shape: "circle" },
    { code: "upper_back", x: 50, y: 24, ty: "-10%", w: 35, shape: "pill" },
    { code: "lower_back", x: 50, y: 46, ty: "-136%", w: 35, shape: "pill" },

    // shoulders
    { code: "shoulder_left", x: 30, tx: "-85%", y: 20, w: 35, shape: "pill" },
    { code: "shoulder_right", x: 70, tx: "-15%", y: 20, w: 35, shape: "pill" },

    // arms
    { code: "arm_left", x: 18, y: 34, ty: "-35%", w: 16, shape: "oval-v" },
    { code: "arm_right", x: 82, y: 34, ty: "-35%", w: 16, shape: "oval-v" },

    // hands
    { code: "hand_left", x: 10, y: 50, ty: "-50%", w: 20, shape: "circle" },
    { code: "hand_right", x: 90, y: 50, ty: "-50%", w: 20, shape: "circle" },

    // hips
    { code: "hips", x: 50, y: 60, ty: "-170%", w: 35, shape: "pill" },

    // knees
    { code: "knee_left", x: 38, tx: "-115%", y: 76, ty: "-75%", w: 16, shape: "oval-v" },
    { code: "knee_right", x: 62, tx: "15%", y: 76, ty: "-75%", w: 16, shape: "oval-v" },

    // feet
    { code: "foot_left", x: 38, tx: "-105%", y: 92, w: 35, shape: "pill" },
    { code: "foot_right", x: 62, tx: "5%", y: 92, w: 35, shape: "pill" }
];
